import { Placeholder, Text } from '@lmn/design-system';
import styled, { css } from 'styled-components';
import { focusRing } from '@/app/components/search/styles/mixins';
import React from 'react';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import { getOptionKey } from '@/app/components/hotel-detail/rooms/roomCommon';
import { useTranslation } from '@/app/components/translations/Translation';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import { useSelectedMealPlan } from '@/app/components/hotel-detail/SelectedMealPlanProvider';
import { keyDownHandler } from '@/app/components/hotel-detail/common/common.helper';
import customCss from '@/app/components/hotel-detail/css/customCss';
import {
  BOARD_CLASS_ICON_INDEX,
  getMealPlanEvoIcon,
} from '@/app/components/search/components/Filters/verticalFilters/Fields/getMealPlanField/getMealPlanField';
import { formatPrice } from '@/app/components/search/utils/formatting.ts';
import usePriceDisplay from '@/app/components/hotel-detail/hooks/usePriceDisplay.ts';

const MEAL_PLAN_ICONS = getMealPlanEvoIcon();

// Placeholder chips shown on mobile while the rooms load: two full 2-column rows. The remaining
// board classes stay hidden there (as unavailable ones always are); desktop shows every option.
const MOBILE_LOADING_CHIPS = 4;

export const BoardClassBarContainer = styled.div<{ $hideBelowLg?: boolean }>`
  display: ${(p) => (p.$hideBelowLg ? 'none' : 'grid')};
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  background-color: white;

  @media ${customCss.breakpoints.lg} {
    display: grid;
    grid-template-columns: none;
    grid-auto-flow: column;
    grid-auto-columns: 1fr;
    gap: 0;
    border-radius: 16px;
    border: 1px solid ${(p) => p.theme.kallisto.color.border.interactive.borderNeutralDefault};
    overflow: visible;
  }
`;

const IconContainer = styled.div<{ $isAvailable?: boolean; $selected?: boolean }>`
  /* Icons are hidden on mobile; only shown from the segmented-control breakpoint up. */
  display: none;
  max-height: 20px;

  /* Match the icon colour to the label: brand when selected, primary when idle, disabled otherwise. */
  color: ${({ theme, $isAvailable, $selected }) =>
    !$isAvailable
      ? theme.kallisto.color.icon.static.iconDisabled
      : $selected
        ? theme.kallisto.color.icon.interactive.iconPrimaryDefault
        : theme.kallisto.color.icon.static.iconPrimary};

  @media ${customCss.breakpoints.lg} {
    display: block;
  }
`;

const LabelContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
`;

const LabelText = styled(Text)`
  line-height: 20px;

  @media ${customCss.breakpoints.lg} {
    line-height: 24px;
  }
`;

const PriceContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  min-height: 20px; /* Ensure consistent height for price row */
`;

const PriceText = styled(Text)``;

const BoardClassOption = styled.div<{
  $selected?: boolean;
  $available?: boolean;
  $showDivider?: boolean;
  $fullWidthMobile?: boolean;
  $loading?: boolean;
}>`
  /* Unavailable options are hidden on mobile — except the chips marked as loading
     placeholders, which keep the bar's space while availability is still unknown. */
  display: ${(p) => (p.$available || p.$loading ? 'grid' : 'none')};
  grid-column: ${(p) => (p.$fullWidthMobile ? '1 / -1' : 'auto')};
  grid-template-rows: auto 1fr auto;
  align-items: center;
  justify-items: center;
  padding: ${({ theme }) => theme.kallisto.space.space100} ${({ theme }) => theme.kallisto.space.space200};
  border-radius: 16px;
  border: ${(p) =>
    p.$selected
      ? `2px solid ${p.theme.kallisto.color.border.interactive.borderPrimaryDefault}`
      : `1px solid ${p.theme.kallisto.color.border.interactive.borderNeutralDefault}`};
  ${(p) => (p.$selected ? `background: white;` : undefined)};

  cursor: ${(p) => (p.$available ? 'pointer' : 'default')};

  &:focus-visible {
    ${focusRing}
  }

  /* Hovering an available option highlights its icon, label and price in the selected (primary) colour. */
  ${(p) =>
    p.$available &&
    css`
      &:hover {
        ${IconContainer} {
          color: ${p.theme.kallisto.color.icon.interactive.iconPrimaryDefault};
        }
        ${LabelText}, ${PriceText} {
          color: ${p.theme.kallisto.color.text.interactive.textPrimaryDefault};
        }
      }
    `}

  position: relative;
  width: auto;
  min-width: unset;
  white-space: nowrap;

  @media ${customCss.breakpoints.lg} {
    /* Desktop shows every option (including unavailable) in the segmented control. */
    display: grid;
    grid-column: auto;
    min-width: 0;
    margin: -1px; /* To cover the border of the container */
    padding: ${({ theme }) => theme.kallisto.space.space150} ${({ theme }) => theme.kallisto.space.space200};

    border: 2px solid
      ${(p) => (p.$selected ? p.theme.kallisto.color.border.interactive.borderPrimaryDefault : 'transparent')};
    white-space: normal;
    text-align: center;

    /* Vertical separator between adjacent meal types.
       Hidden on both sides of the selected option, whose border is the boundary. */
    &::before {
      content: '';
      display: ${(p) => (p.$showDivider ? 'block' : 'none')};
      position: absolute;
      left: 0;
      top: 16px;
      bottom: 16px;
      width: 1px;
      background-color: ${(p) => p.theme.kallisto.color.border.static.borderNeutral};
    }
  }
`;

type BoardClassBarProps = {
  rooms: RoomType[];
  onSelectBoardClass: (boardClass: BoardClassType) => void;
  initialBoardClass?: BoardClassType;
} & React.HTMLAttributes<HTMLElement>;

export function EvoBoardClassBar(props: BoardClassBarProps) {
  const { rooms, onSelectBoardClass, initialBoardClass, ...rest } = props;

  const { t } = useTranslation();
  const { isLoadingRooms } = useOffersContext();
  const { localization } = usePageWrapperData();
  const urlSearchParams = useUrlSearchParams();
  const { selectedMealPlan } = useSelectedMealPlan();
  const { showTotalPrice, familyTotalPrice } = usePriceDisplay();
  // A family delta (MPH-1292) and a hotel-only delta carry no unit: a hotel-only price is already a whole-stay
  // figure, so it is not per person either (MPH-1363). For a package, the price toggler's total mode still
  // labels its deltas "total" on purpose, and per-person mode labels them "pp".
  const priceUnit =
    familyTotalPrice || urlSearchParams.accommodationOnly
      ? ''
      : t(showTotalPrice ? 'vacation.oss.cards.total' : 'vacation.oss.cards.pp');

  const [selectedBoardClass, setSelectedBoardClass] = React.useState<BoardClassType | undefined>(initialBoardClass);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // The "selected" label and the price differences are anchored to the meal plan currently selected on the
  // page — not to the locally highlighted tab (selectedBoardClass). They only move when the page selection changes.
  const selectedMealPlanBoardClass = selectedMealPlan?.boardClass;

  // Get unique board classes and their min prices
  const boardClassOptions = React.useMemo(() => {
    // Cheapest config is the same under either unit (total is pp × the same pax).
    const map = new Map<BoardClassType, { minPrice: number; minTotalPrice: number }>();
    rooms.forEach((room) => {
      room.configurations.forEach((config) => {
        const key = config.boardClass;
        if (!map.has(key) || config.finalPrice < map.get(key)!.minPrice) {
          map.set(key, { minPrice: config.finalPrice, minTotalPrice: config.finalTotalPrice });
        }
      });
    });

    // Price differences are shown relative to the meal plan selected on the page (its actual price).
    const selectedMealPlanPrice =
      (showTotalPrice ? selectedMealPlan?.finalTotalPrice : selectedMealPlan?.finalPrice) ?? null;

    // Return all board class types, with availability info and the price difference vs the selected meal plan.
    return Object.keys(BOARD_CLASS_ICON_INDEX).map((boardClass) => {
      const typedBoardClass = boardClass as BoardClassType;
      const availableOption = map.get(typedBoardClass);

      // The selected meal plan's option shows a "selected" label instead of a price; every other available
      // option shows its cheapest config's increment/decrement relative to the selected meal plan.
      let priceDifference = null;
      if (availableOption && typedBoardClass !== selectedMealPlanBoardClass && selectedMealPlanPrice !== null) {
        const optionPrice = showTotalPrice ? availableOption.minTotalPrice : availableOption.minPrice;
        priceDifference = optionPrice - selectedMealPlanPrice;
      }

      return {
        boardClass: typedBoardClass,
        priceDifference,
        isAvailable: !!availableOption,
      };
    });
  }, [
    rooms,
    selectedMealPlanBoardClass,
    selectedMealPlan?.finalPrice,
    selectedMealPlan?.finalTotalPrice,
    showTotalPrice,
  ]);

  // Effect to handle initialBoardClass prop changes
  React.useEffect(() => {
    if (initialBoardClass && rooms.length > 0) {
      // Search directly in rooms to check if the board class is available
      const hasMatchingBoardClass = rooms.some((room) =>
        room.configurations.some((config) => config.boardClass === initialBoardClass)
      );
      if (hasMatchingBoardClass) {
        setSelectedBoardClass(initialBoardClass);
      }
    }
  }, [initialBoardClass, rooms]);

  // Set default selected board class when no initialBoardClass is provided
  React.useEffect(() => {
    if (!initialBoardClass && !selectedBoardClass && boardClassOptions.length > 0) {
      // Select the first available board class
      const firstAvailable = boardClassOptions.find((option) => option.isAvailable);
      if (firstAvailable) {
        setSelectedBoardClass(firstAvailable.boardClass);
      }
    }
  }, [initialBoardClass, selectedBoardClass, boardClassOptions]);

  // Scroll to selected option when it changes
  React.useEffect(() => {
    if (selectedBoardClass && containerRef.current) {
      const selectedElement = containerRef.current.querySelector(
        `[data-board-class="${selectedBoardClass}"]`
      ) as HTMLElement;
      if (selectedElement) {
        const container = containerRef.current;
        // Calculate the scroll position to center the element
        const scrollLeft =
          selectedElement.offsetLeft -
          container.offsetLeft -
          container.clientWidth / 2 +
          selectedElement.clientWidth / 2;

        container.scrollTo({
          left: scrollLeft,
          behavior: 'smooth',
        });
      }
    }
  }, [selectedBoardClass]);

  // While the rooms load there is nothing to derive availability or prices from, so instead of
  // collapsing on mobile (where unavailable chips are hidden) the bar keeps its space with two
  // rows of neutral placeholder chips — availability is unknown, so none renders a label or a
  // selection. Desktop still shows the full labelled segmented control.
  const isLoadingSkeleton = isLoadingRooms && rooms.length === 0;
  const groupLabel = isLoadingSkeleton ? '' : t('vacation.select.room_panel.meal_type');

  // On mobile, unavailable options are hidden (CSS). When the count of *available* options is odd, the
  // last available one sits alone on its row, so it spans the full width. Desktop still shows everything.
  // (Loading placeholder chips are always MOBILE_LOADING_CHIPS, an even count, so none spans.)
  const availableOptions = boardClassOptions.filter((option) => option.isAvailable);
  const mobileFullWidthBoardClass =
    !isLoadingSkeleton && availableOptions.length % 2 === 1
      ? availableOptions[availableOptions.length - 1].boardClass
      : undefined;

  // MPH-1268: below lg a single available board class leaves one full-width "Selected" chip with
  // nothing to choose, so the bar is hidden there. Desktop keeps the segmented control, which also
  // lists the unavailable board classes. The loading placeholders keep showing until the rooms land.
  const hideBelowLg = !isLoadingSkeleton && availableOptions.length === 1;

  return (
    <BoardClassBarContainer
      ref={containerRef}
      $hideBelowLg={hideBelowLg}
      role={groupLabel ? 'group' : undefined}
      aria-label={groupLabel || undefined}
      {...rest}
    >
      {boardClassOptions.map(({ boardClass, priceDifference, isAvailable }, index) => {
        const textColor = isAvailable
          ? selectedBoardClass === boardClass
            ? 'textPrimaryDefault'
            : 'textPrimary'
          : 'textDisabled';
        // Show the left divider unless it would sit next to the selected option (its border is the boundary).
        const isSelected = (option?: (typeof boardClassOptions)[number]) =>
          !!option && selectedBoardClass === option.boardClass && option.isAvailable;
        const showDivider =
          index > 0 && !isSelected(boardClassOptions[index]) && !isSelected(boardClassOptions[index - 1]);
        const isOptionSelected = selectedBoardClass === boardClass && isAvailable;
        const handleSelect = () => {
          if (!isAvailable) return;
          setSelectedBoardClass(boardClass);
          onSelectBoardClass(boardClass);
        };
        return (
          <BoardClassOption
            key={boardClass}
            data-board-class={boardClass}
            // While the placeholders load the chips have no name, no state and no action, so
            // they carry no button semantics — screen readers would otherwise announce a row
            // of nameless disabled buttons.
            role={isLoadingSkeleton ? undefined : 'button'}
            tabIndex={isLoadingSkeleton ? undefined : isAvailable ? 0 : -1}
            aria-pressed={isLoadingSkeleton ? undefined : isOptionSelected}
            aria-disabled={isLoadingSkeleton ? undefined : !isAvailable}
            $selected={isOptionSelected}
            $available={isAvailable}
            $showDivider={showDivider}
            $fullWidthMobile={boardClass === mobileFullWidthBoardClass}
            $loading={isLoadingSkeleton && index < MOBILE_LOADING_CHIPS}
            onClick={isLoadingSkeleton ? undefined : handleSelect}
            onKeyDown={!isLoadingSkeleton && isAvailable ? keyDownHandler(handleSelect) : undefined}
          >
            <IconContainer $isAvailable={isAvailable} $selected={selectedBoardClass === boardClass}>
              {MEAL_PLAN_ICONS[BOARD_CLASS_ICON_INDEX[boardClass]]}
            </IconContainer>
            <LabelContainer>
              {isLoadingSkeleton ? (
                <>
                  {/* Which board classes end up shown on mobile is unknown while loading, so its
                      chips stay neutral placeholders; desktop always shows every label. */}
                  <div
                    css={`
                      height: 20px;
                      display: flex;
                      align-items: center;

                      @media ${customCss.breakpoints.lg} {
                        display: none;
                      }
                    `}
                  >
                    <Placeholder width="56px" height="16px" />
                  </div>
                  <LabelText
                    variant="Body"
                    weight="bold"
                    color={textColor}
                    css={`
                      display: none;

                      @media ${customCss.breakpoints.lg} {
                        display: block;
                      }
                    `}
                  >
                    {t(getOptionKey(boardClass as BoardClassType, urlSearchParams.accommodationOnly))}
                  </LabelText>
                </>
              ) : (
                <LabelText variant="Body" weight="bold" color={textColor}>
                  {t(getOptionKey(boardClass as BoardClassType, urlSearchParams.accommodationOnly))}
                </LabelText>
              )}
            </LabelContainer>
            <PriceContainer>
              {isLoadingRooms ? (
                <div
                  css={`
                    height: 20px;
                    display: flex;
                    align-items: center;
                  `}
                >
                  <Placeholder width="80px" height="14px" />
                </div>
              ) : (
                <PriceText variant="BodySmall" color={textColor}>
                  {!isAvailable
                    ? t('vacation.oss.comparison.modal.not_available')
                    : selectedMealPlanBoardClass === boardClass
                      ? t('vacation.select.cc.rooms.meal.selected')
                      : priceDifference !== null
                        ? `${t('vacation.common.price.from')} ${priceDifference >= 0 ? '+' : '-'}${formatPrice({ price: Math.abs(priceDifference), localization })}${priceUnit ? ` ${priceUnit}` : ''}`
                        : t('vacation.oss.comparison.modal.not_available')}
                </PriceText>
              )}
            </PriceContainer>
          </BoardClassOption>
        );
      })}
    </BoardClassBarContainer>
  );
}
